import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactHtmlParser from 'react-html-parser';
import { FormattedMessage, useIntl } from 'react-intl/dist';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Components
import Form from '../../../lib/wrappers/Form';
import PreferencesPageAddressViewer from './PreferencesPageAddressViewer';
import PreferencesPageControls from './PreferencesPageControls';
import UniversalInput from '../../../lib/wrappers/UniversalInput';
import ValidationErrorMessage from '../../ssap/notifications/ValidationErrorMessage';
import Tooltip from '../../common/tooltip/Tooltip';
import DropDownMenu from '../../../lib/inputs/DropDownMenu';
import Button from '../../../components/common/button/Button';
import ConditionalRendering from '../../../components/common/conditional-rendering/ConditionalRendering';

// Hooks
import { useLocation } from '../../../hooks/useLocation';

// Constants
import { commonModalTypes as ModalTypes } from '../../../constants/common/modals/commonModalTypes';
import { configs } from '../../../constants/configs/configs';
import { dashboardModalTypes } from '../../../constants/individualPortal/dashboard/modals/dashboardModalTypes';
import { INPUT_TYPES } from '../../../constants/inputs/inputTypes';
import { preventerTools } from '../../../utils/tools/preventerTools';
import { VALIDATION_ERROR_MESSAGES } from '../../../constants/inputs/validationErrorMessages';
import { VALIDATION_TYPES } from '../../../constants/inputs/validationTypes';
import { ALL_USER_ROLES } from '../../../constants/roles/userRoleTypes';
import { phoneNumberTools } from '../../../utils/tools/phoneNumberTools';
import { INPUT_TYPES as BUTTON_INPUT_TYPES } from '../../../components/common/form/constants/fieldTypes';

//Utils
import { omit } from '../../../utils/miscellaneous/objectHelpers';

const CommunicationPreferencesPage = props => {
    const COMMUNICATION_METHOD_SUCCESS_ID = 'preferences_page.preferences_save_success';
    const EXIT_CLICK = 'onFormExit';
    const MAIL_ADDRESS_SUCCESS_ID = 'preferences_page.mailing_address_save_success';
    const PREFERENCES_FORM = 'preferencesForm';
    const SUBMIT_CLICK = 'onFormSubmit';

    const {
        actions: {
            fetchExchangeDetails,
            fetchPreferences,
            savePreferences,
            showModal
        },
        data,
        form,
        history,
        isDashboardLocation,
        isState,
        methods,
        switchBackLink,
        updateData,
        environment: {
            links: { PRIVACY_POLICY, PHONE },
            user: userData,
            exchangeName,
            stateCode
        },
    } = props;

    const {
        emailAddress,
        emailMe,
        emailVerified,
        mailingAddressUpdateAllowed,
        mailingAddress,
        paperlessNotices,
        noticeOptOut,
        phoneNumber,
        phoneVerified,
        preferredSpokenLanguage,
        preferredWrittenLanguage,
        paperless1095,
        showNoticeOptOut,
        smsUnsubscribed,
        smsVerified,
        textMe,
        userRecordExists,
    } = data;

    const [exchangePhone, setExchangePhone] = useState(PHONE);

    useEffect(() => {
        if (mailingAddress.streetAddress1 === null && mailingAddressUpdateAllowed) {
            showModal({
                modalType: dashboardModalTypes.MAILING_ADDRESS_UPDATING_MODAL,
                modalData: {
                    actionNames: {
                        EXIT_CLICK,
                        SUBMIT_CLICK,
                    },
                    data,
                    handleAddressSave,
                    isDashboardLocation: false,
                    mailingAddress: data.mailingAddress,
                },
            });
        }
    }, []);

    const getExchangeDetails = async () => {
        const response = await fetchExchangeDetails();
        const { success, payload } = response;
        if (success) {
            const { exchangeSmsPhoneNumber } = payload;
            setExchangePhone(exchangeSmsPhoneNumber);
        }
    };
    useEffect(() => {
        if (smsUnsubscribed) {
            getExchangeDetails();
        }
    }, [smsUnsubscribed])

    const intlApi = useIntl();
    const { fromCAP } = useLocation();

    const { isFormSubmitted, isFormOnTopLevelValid } = preventerTools.provideObject(form[PREFERENCES_FORM]);

    const onFieldChange = async (...args) => {
        const [newData, , , [name, value]] = args;

        const saveResult = await updatePreferences({ ...data, [name]: value });

        if (saveResult?.success) {
            updateData(data => ({
                ...data,
                ...newData,
                textMe:
                    (name === 'phoneNumber' && !value) || (name === 'textMe' && !data.phoneNumber)
                        ? false
                        : newData.textMe ?? data.textMe,
                emailMe:
                    (name === 'emailAddress' && !value) || (name === 'emailMe' && !data.emailAddress)
                        ? false
                        : newData.emailMe ?? data.emailMe,
            }));
        } else {
            handleFailureModal();
        }
    };

    const onLanguageChange = async (name, value) => {
        if (data[name] !== value) {
            const saveResult = await updatePreferences({ ...data, [name]: value });
            if (saveResult?.success) {
                updateData(data => ({
                    ...data,
                    [name]: value,
                }));
            } else {
                handleFailureModal();
            }
        }
    };

    const updatePreferences = (updatedData, successMessage = '') => {
        return savePreferences(updatedData).then(res => {
            if (res?.success && !configs.ENV.DEVELOPMENT) {
                updateData({
                    ...res.payload,
                    noUpdateOfCommPrefOnEmailAddressError: true,
                });
            }
            return Promise.resolve({
                errorMessage: res?.message,
                success: res?.success,
                successMessage,
            });
        });
    };

    const handleFailureModal = () => {
        showModal({
            modalType: dashboardModalTypes.FAILURE_MODAL,
            modalData: {},
        });
    };

    const showSaveResultModal = (callback, saveResult) => {
        showModal({
            modalType: ModalTypes.SAVE_RESULT_MODAL,
            modalData: {
                callback,
                saveResult,
            },
        });
    };

    const navigateToCAP = () => (global.location = switchBackLink);

    const getNavigationPath = () =>
        userData.role === ALL_USER_ROLES.INDIVIDUAL
            ? `${configs.routes.HOME}${userData.landingPage.substr(1)}`
            : configs.routes.INDPORTAL;

    const handlePreferencesSubmit = async dataExceptAddress => {
        const saveResult = await updatePreferences({ ...data, ...dataExceptAddress }, COMMUNICATION_METHOD_SUCCESS_ID);
        if (saveResult?.success) {
            showSaveResultModal(fromCAP ? navigateToCAP : () => history.push(getNavigationPath()), saveResult);
        } else {
            handleFailureModal();
        }
    };

    const handleAddressSave = async (data, callback) => {
        const saveResult = await updatePreferences(data, MAIL_ADDRESS_SUCCESS_ID);
        if (saveResult?.success) {
            showSaveResultModal(callback, saveResult);
        } else {
            handleFailureModal();
        }
    };

    const handleClick = () => {
        fromCAP ? navigateToCAP() : history.push(getNavigationPath());
    };

    const handleSwitchingToAddressUpdate = () => {
        showModal({
            modalType: dashboardModalTypes.MAILING_ADDRESS_UPDATING_MODAL,
            modalData: {
                actionNames: {
                    EXIT_CLICK,
                    SUBMIT_CLICK,
                },
                data,
                handleAddressSave,
                isDashboardLocation,
                mailingAddress: data.mailingAddress,
            },
        });
    };

    const handleContactUpdate = async (_, name, value) => {
        const response = await fetchPreferences();
        const {
            success,
            payload
        } = response || {};

        if (success) {
            const {
                emailAddress,
                emailMe,
                mailingAddress,
                mailingAddress: {
                    state
                },
                phoneNumber,
                preferredSpokenLanguage,
                preferredWrittenLanguage,
                textMe
            } = payload;

            updateData({
                ...payload,
                [name]: value,
                textMe: phoneNumber ? textMe : false,
                emailMe: emailAddress ? emailMe : false,
                preferredSpokenLanguage: preferredSpokenLanguage ? preferredSpokenLanguage : 'English',
                preferredWrittenLanguage: preferredWrittenLanguage ? preferredWrittenLanguage : 'English',
                mailingAddress: {
                    ...mailingAddress,
                    ...(state === null ? { state: stateCode } : {})
                },
                noUpdateOfCommPrefOnEmailAddressError: true
            });
        }
    };

    const handleChangeEmailModal = () => {
        showModal({
            modalType: dashboardModalTypes.CHANGE_EMAIL_MODAL,
            modalData: {
                actionNames: {
                    EXIT_CLICK,
                    SUBMIT_CLICK,
                },
                data: data,
                name: 'emailAddress',
                type: INPUT_TYPES.REGULAR_INPUT,
                validationType: VALIDATION_TYPES.EMAIL,
                updateData: handleContactUpdate,
                value: emailAddress
            },
        });
    };

    const handleChangePhoneModal = () => {
        showModal({
            modalType: dashboardModalTypes.CHANGE_PHONE_MODAL,
            modalData: {
                actionNames: {
                    EXIT_CLICK,
                    SUBMIT_CLICK,
                },
                data: data,
                name: 'phoneNumber',
                type: INPUT_TYPES.PHONE_NUMBER_INPUT,
                validationType: VALIDATION_TYPES.PHONE_NUMBER,
                updateData: handleContactUpdate,
                value: phoneNumber
            },
        });
    };

    const handleVerifyPhoneModal = () => {
        showModal({
            modalType: dashboardModalTypes.VERIFY_PHONE_MODAL,
            modalData: {
                actionNames: {
                    EXIT_CLICK,
                    SUBMIT_CLICK,
                },
                data: data,
                name: 'phoneNumber',
                validationType: VALIDATION_TYPES.PHONE_NUMBER,
                updateData: handleContactUpdate,
            },
        });
    };

    const handleMailingAddressExtension = () => {
        methods[PREFERENCES_FORM][EXIT_CLICK]();
    };

    const getLinkToDashboard = content => <Link to={configs.routes.INDPORTAL}>{content}</Link>;

    const checkTopLevelValidity = () => {
        return (!paperlessNotices && !paperless1095) || emailMe || textMe;
    };

    return (
        <Fragment>
            <div className="height-7" />

            <Form
                {...{
                    data: omit(data, 'mailingAddress'),
                    formName: PREFERENCES_FORM,
                    onLocalFormExit: handleSwitchingToAddressUpdate,
                    onLocalFormSubmit: handlePreferencesSubmit,
                    topLevelValidity: checkTopLevelValidity(),
                }}
            >
                <div className="gi-panel">
                    <h3 className="my-4">
                        <FormattedMessage id="ssap.page_title.communication_prefernces" values={{ exchangeName }} />
                    </h3>
                    <p>
                        <FormattedMessage id="preferences_page.warnings.asterisk" />
                    </p>

                    <div className="grid-row align-items-center my-3">
                        <div className="col-3 p-4">
                            <span className="required-true">
                                <FormattedMessage id="ssap.input.label.phone_number" />
                            </span>
                            <Tooltip
                                id="preferences_phone_number"
                                index="preferences_phone_number"
                                content="preferences.tooltip.phone_number"
                                icon="question-circle"
                                triggerDecorationClassName="border-none"
                            />
                        </div>
                        <div className="col-9 p-4">
                            <div className="gi-alert__body gap-4">
                                <ConditionalRendering isTrue={!!phoneNumber}>
                                    <span data-test-id="preferences_phone_number">
                                        {phoneNumberTools.getAdjustedPhoneNumber(phoneNumber)}
                                    </span>
                                    <span className={`app-status-indicator font-weight-normal badge py-2 px-3 ${phoneVerified ? 'category_status--completed' : 'category_status--pending'}`} >
                                        {phoneVerified ? (
                                            <>
                                                <FontAwesomeIcon icon="check-circle" className='mr-2' />
                                                <FormattedMessage id="common.status.verified" />
                                            </>
                                        ) : (
                                                intlApi.formatMessage({ id: "common.status.unverified", defaultMessage: " " }).trim()
                                        )}
                                    </span>
                                </ConditionalRendering>
                                <Button
                                    id="preferences_phone_number_button"
                                    dataTestId="preferences_phone_number_button"
                                    name="phoneNumber"
                                    classes={['usa-button', 'gi-button__outline']}
                                    type={BUTTON_INPUT_TYPES.BUTTON}
                                    btnText={`ssap.button.${phoneNumber ? 'change_phone' : 'add_phone'}`}
                                    icon="edit"
                                    iconPlacement="START"
                                    handleClick={handleChangePhoneModal}
                                />
                            </div>
                        </div>
                        <ConditionalRendering isTrue={!!phoneNumber}>
                            <div className="col-3"></div>
                            <ConditionalRendering isTrue={!smsUnsubscribed}>
                                <ConditionalRendering isTrue={!textMe && !phoneVerified}>
                                    <div className="col-9 border p-4 bg-light">
                                        <div className="center-flex">
                                            <p className="mt-0">
                                                <FormattedMessage id="preferences_page.verify_phone_message" />
                                            </p>
                                            <Button
                                                id="preferences_phone_number_button"
                                                dataTestId="preferences_phone_number_button"
                                                name="phoneNumber"
                                                classes={['usa-button', 'gi-button__outline']}
                                                type={BUTTON_INPUT_TYPES.BUTTON}
                                                btnText="ssap.button.verify_phone"
                                                handleClick={handleVerifyPhoneModal}
                                            />
                                        </div>
                                    </div>
                                </ConditionalRendering>
                                <ConditionalRendering isTrue={textMe || (phoneVerified && smsVerified)}>
                                    <div className="col-9 border p-4 bg-light">
                                        <UniversalInput
                                            label={{
                                                text: 'ssap.input.label.text_me',
                                                ...(isState.PA
                                                    ? {
                                                        additionalText: (
                                                            <FormattedMessage
                                                                id="ssap.input.label.text_me.additional_chunk"
                                                                values={{
                                                                    link: (
                                                                        <a
                                                                            className="usa-link--external"
                                                                            href={PRIVACY_POLICY}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                        >
                                                                            <FormattedMessage id="ssap.start.primary_contact_info.link_text" />
                                                                        </a>
                                                                    ),
                                                                }}
                                                            />
                                                        ),
                                                    }
                                                    : {}),
                                            }}
                                            inputData={{
                                                isInputRequired: false,
                                                type: INPUT_TYPES.SWITCH_CHECKBOX,
                                                name: 'textMe',
                                                classes: ['tablet:grid-col-6 tablet:grid-offset-4 layout--02'],
                                                checked: textMe,
                                                disabled: paperlessNotices && textMe && !emailMe,
                                                tooltipText: VALIDATION_ERROR_MESSAGES.CHOOSE_DELIVERY_CHANNEL,
                                                value: textMe,
                                                currentData: {},
                                                fields: [],
                                            }}
                                            inputActions={{
                                                onFieldChange,
                                            }}
                                        />
                                    </div>
                                </ConditionalRendering>
                            </ConditionalRendering>
                            <ConditionalRendering isTrue={smsUnsubscribed || (phoneVerified && !smsVerified && !textMe)}>
                                <div className="col-9">
                                    <div
                                        style={{ backgroundPosition: '15px center' }}
                                        className="usa-alert usa-alert--info"
                                    >
                                        <div className="usa-alert__body">
                                            <p className="usa-alert__text">
                                                {!smsUnsubscribed ? (
                                                    <FormattedMessage id="preferences_page.phone_unsupported_message" />
                                                ) : (
                                                        <span>
                                                            {ReactHtmlParser(intlApi.formatMessage(
                                                                { id: 'preferences_page.phone_unsubscribed_message' },
                                                                { exchangeName, exchangePhone: phoneNumberTools.formatPhoneNumber(exchangePhone), phone: phoneNumberTools.getAdjustedPhoneNumber(phoneNumber) }
                                                            ))}
                                                        </span>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </ConditionalRendering>
                        </ConditionalRendering>
                    </div>
                    <div className="grid-row align-items-center my-3">
                        <div className="col-3 p-4">
                            <span className="required-true">
                                <FormattedMessage id="ssap.input.label.email" />
                            </span>
                            <Tooltip
                                id="preference_email"
                                index="preference_email"
                                content="preferences.tooltip.email"
                                icon="question-circle"
                                triggerDecorationClassName="border-none"
                            />
                        </div>
                        <div className="col-9 p-4">
                            <div className="gi-alert__body gap-4">
                                <ConditionalRendering isTrue={!!emailAddress}>
                                    <span data-test-id="preferences_email">{emailAddress}</span>
                                    <span className={`app-status-indicator font-weight-normal badge py-2 px-3 ${emailVerified ? 'category_status--completed' : 'category_status--pending'}`} >
                                        {emailVerified ? (
                                            <>
                                                <FontAwesomeIcon icon="check-circle" className='mr-2' />
                                                <FormattedMessage id="common.status.verified" />
                                            </>
                                        ) : (
                                                intlApi.formatMessage({ id: "common.status.unverified", defaultMessage: " " }).trim()
                                        )}
                                    </span>
                                </ConditionalRendering>
                                <ConditionalRendering isTrue={!userRecordExists}>
                                    <Button
                                        id="preferences_email_button"
                                        dataTestId="preferences_email_button"
                                        name="emailAddress"
                                        classes={['usa-button', 'gi-button__outline']}
                                        type={BUTTON_INPUT_TYPES.BUTTON}
                                        btnText={`ssap.button.${emailAddress ? 'change_email' : 'add_email'}`}
                                        icon="edit"
                                        iconPlacement="START"
                                        handleClick={handleChangeEmailModal}
                                        disabled={userRecordExists}
                                    />
                                </ConditionalRendering>
                            </div>
                        </div>
                        <ConditionalRendering isTrue={!!emailAddress}>
                            <div className="col-3"></div>
                            <div className="col-9 border p-4 bg-light">
                                <UniversalInput
                                    label={{
                                        text: 'ssap.input.label.email_me',
                                    }}
                                    inputData={{
                                        isInputRequired: false,
                                        type: INPUT_TYPES.SWITCH_CHECKBOX,
                                        name: 'emailMe',
                                        classes: ['tablet:grid-col-6 tablet:grid-offset-4 layout--02'],
                                        checked: emailMe,
                                        disabled: paperlessNotices && emailMe && !textMe,
                                        tooltipText: VALIDATION_ERROR_MESSAGES.CHOOSE_DELIVERY_CHANNEL,
                                        value: emailMe,
                                        currentData: {},
                                        fields: [],
                                    }}
                                    inputActions={{
                                        onFieldChange,
                                    }}
                                />
                            </div>
                        </ConditionalRendering>
                        {showNoticeOptOut ? (
                            <Fragment>
                                <div className="col-3 p-4">
                                    <FormattedMessage id="ssap.input.label.marketing_communications" />
                                </div>
                                <div className="col-9 border p-4 bg-light my-5">
                                    <UniversalInput
                                        label={{
                                            text: 'ssap.input.label.block_marketing_communications',
                                        }}
                                        inputData={{
                                            isInputRequired: false,
                                            type: INPUT_TYPES.SWITCH_CHECKBOX,
                                            name: 'noticeOptOut',
                                            classes: ['tablet:grid-col-6 tablet:grid-offset-4 layout--02'],
                                            checked: noticeOptOut,
                                            value: noticeOptOut,
                                            currentData: {},
                                            fields: [],
                                        }}
                                        inputActions={{
                                            onFieldChange,
                                        }}
                                    />
                                </div>
                            </Fragment>
                        ) : null}
                    </div>
                </div>

                <div className="gi-panel">
                    <h3 className="my-4">
                        <FormattedMessage id="ssap.page_title.mailing_prefernces" />
                    </h3>
                    <p>
                        <FormattedMessage id="ssap.subheader.mailing_prefernces" values={{
                            exchangeName,
                            link: getLinkToDashboard(
                                <FormattedMessage
                                    id='ssap.subheader.mailing_prefernces.link'
                                />
                            )
                        }} />
                    </p>

                    <PreferencesPageAddressViewer
                        {...{
                            mailingAddress,
                            getLinkToDashboard,
                            handleMailingAddressExtension,
                            mailingAddressUpdateAllowed,
                        }}
                    />

                    <h3 className="body-display-spl-text my-4">
                        <FormattedMessage id="ssap.input.option.delivery_paperless" />
                    </h3>
                    <p>
                        <FormattedMessage id="preferences_page.warnings.notices_delivery" />{' '}
                    </p>

                    <div className="grid-row my-5">
                        <div className="col-3 p-4">
                            <FormattedMessage id="preferences_page.warnings.notices" />
                        </div>
                        <div className="col-9 border p-4 bg-light">
                            <UniversalInput
                                label={{
                                    text: 'ssap.input.label.delivery_paperless',
                                }}
                                inputData={{
                                    isInputRequired: false,
                                    type: INPUT_TYPES.SWITCH_CHECKBOX,
                                    name: 'paperlessNotices',
                                    classes: ['tablet:grid-col-6 tablet:grid-offset-4 layout--02'],
                                    checked: paperlessNotices,
                                    disabled: !paperlessNotices && !emailMe && !textMe,
                                    tooltipText: VALIDATION_ERROR_MESSAGES.CHOOSE_DELIVERY_CHANNEL,
                                    value: paperlessNotices,
                                    currentData: {},
                                    fields: [],
                                }}
                                inputActions={{
                                    onFieldChange,
                                }}
                            />
                        </div>
                        <div className="col-3"></div>
                        <div className="col-9 p-4">
                            <FormattedMessage id="preferences_page.send_you_text_message" />
                        </div>
                    </div>
                    <div className="grid-row my-3">
                        <div className="col-3 p-4">
                            <FormattedMessage id="preferences_page.warnings.tax_form" />
                        </div>
                        <div className="col-9 border p-4 bg-light">
                            <UniversalInput
                                label={{
                                    text: 'ssap.input.label.delivery_paperless',
                                }}
                                inputData={{
                                    isInputRequired: false,
                                    type: INPUT_TYPES.SWITCH_CHECKBOX,
                                    name: 'paperless1095',
                                    classes: ['tablet:grid-col-6 tablet:grid-offset-4 layout--02'],
                                    checked: paperless1095,
                                    disabled: !paperless1095 && !emailMe && !textMe,
                                    tooltipText: VALIDATION_ERROR_MESSAGES.CHOOSE_DELIVERY_CHANNEL,
                                    value: paperless1095,
                                    currentData: {},
                                    fields: [],
                                }}
                                inputActions={{
                                    onFieldChange,
                                }}
                            />
                        </div>
                        <div className="col-3"></div>
                        <div className="col-9 p-4">
                            <FormattedMessage
                                id="preferences_page.warnings.tax_form_delivery"
                                values={{ exchangeName }}
                            />
                        </div>
                    </div>

                    <ValidationErrorMessage
                        isVisible={isFormSubmitted && !isFormOnTopLevelValid}
                        messageText={VALIDATION_ERROR_MESSAGES.CHOOSE_DELIVERY_CHANNEL}
                        errorClasses={['margin-l-20', 'margin-b-10']}
                    />
                </div>

                <div className="gi-panel">
                    <h3 className="my-4">
                        <FormattedMessage id="common.language" />
                    </h3>
                    <p>
                        <FormattedMessage
                            id="preferences_page.warnings.tell_about_language"
                            values={{ exchangeName }}
                        />
                    </p>

                    <div className="grid-row my-3 p-4">
                        <div className="mobile-lg:grid-col-12 tablet:grid-col-4 desktop:grid-col-3 p-2">
                            <span className="required-true">
                                <FormattedMessage id="ssap.input.label.preferred_spoken_language" />
                            </span>
                            <Tooltip
                                id="preferences_language_spoken"
                                index="preferences_language_spoken"
                                content="preferences.tooltip.language"
                                icon="question-circle"
                                triggerDecorationClassName="border-none"
                            />
                        </div>
                        <div className="mobile-lg:grid-col-12 tablet:grid-col-8 desktop:grid-col-9">
                            <div className="grid-row">
                                <span className="mobile-lg:grid-col-12 tablet:grid-col-3 desktop:grid-col-2 p-2">{preferredSpokenLanguage}</span>
                                <span className="mobile-lg:grid-col-12 tablet:grid-col-9 desktop:grid-col-10">
                                    <DropDownMenu
                                        data={data}
                                        onFieldChange={onLanguageChange}
                                        spokenOrWrittenLanguage="preferredSpokenLanguage"
                                    />
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="grid-row my-3 p-4">
                        <div className="mobile-lg:grid-col-12 tablet:grid-col-4 desktop:grid-col-3 p-2">
                            <span className="required-true">
                                <FormattedMessage id="ssap.input.label.preferred_written_language" />
                            </span>
                            <Tooltip
                                id="preferences_language_spoken"
                                index="preferences_language_spoken"
                                content="preferences.tooltip.language"
                                icon="question-circle"
                                triggerDecorationClassName="border-none"
                            />
                        </div>
                        <div className="mobile-lg:grid-col-12 tablet:grid-col-8 desktop:grid-col-9">
                            <div className="grid-row">
                                <span className="mobile-lg:grid-col-12 tablet:grid-col-3 desktop:grid-col-2 p-2">{preferredWrittenLanguage}</span>
                                <span className="mobile-lg:grid-col-12 tablet:grid-col-9 desktop:grid-col-10">
                                    <DropDownMenu
                                        data={data}
                                        onFieldChange={onLanguageChange}
                                        spokenOrWrittenLanguage="preferredWrittenLanguage"
                                    />
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="height-7" />

                <PreferencesPageControls
                    {...{
                        actionName: SUBMIT_CLICK,
                        formName: PREFERENCES_FORM,
                        handleClick
                    }}
                />
            </Form>
        </Fragment>
    );
};

export default CommunicationPreferencesPage;
